import { ActionButtonData, MessageDto, MessageSuggestionsDto, MessageType } from 'client/types';
import { Board, InternalChatState, Message, MessageFrom, MessageMethod } from 'types';
import state from 'store';
import initParams from 'store/initParams';

/**
 * Obtiene a partir de un DTO de un mensaje, el mensaje que corresponde
 * @param dto
 */
export function transformMessage(dto: MessageDto | Message): Message {
  if (typeof dto === 'object' && 'from' in dto && 'isUser' in dto) {
    return dto;
  }

  // Transform object only if it is a MessageDto
  const { id, text, type, timestamp, context, suggestions, redirectTo, content } = dto as MessageDto;

  const transformedMessage: Message = {
    id,
    text,
    suggestions,
    board: transformBoard(dto as MessageDto),
    from: (type === 'USER' ? 'USER' : 'BACKEND') as MessageFrom,
    isUser: type.toUpperCase() === 'USER',
    isAssistant: type.toUpperCase() === 'ASSISTANT',
    isSystem: false,
    hasBoard: typeof content !== 'undefined' && (content?.components?.length ?? 0) > 0,
    method: 'TEXT' as MessageMethod,
    variant: dto.variant,
    feedback: null,
    timestamp,
    context,
    type: type.toUpperCase() as MessageType,
    redirectTo,
  };

  return transformedMessage;
}

/**
 * Construye un mensaje a partir de un texto y un tipo
 * @param text
 * @param type
 * @param timestamp
 * @param from
 * @param method
 * @param messageId
 * @param suggestions
 * @param action
 * @param variant
 */
export function buildMessage(
  text: string,
  type: MessageType,
  timestamp: number,
  from: MessageFrom,
  method: MessageMethod,
  messageId?: string,
  suggestions?: MessageSuggestionsDto[],
  action?: ActionButtonData,
  variant?: 'REQUEST' | 'RESPONSE' | 'PROGRESS' | 'OUTBOUND',
): Message {
  const id = typeof messageId === 'string' ? messageId : `user-${timestamp}`;

  return {
    id,
    text,
    type,
    timestamp,
    from,
    method,
    suggestions,
    action,
    variant,
    isSystem: from === 'SYSTEM',
    isUser: type === 'USER',
    isAssistant: type === 'ASSISTANT',
    feedback: null,
    hasBoard: false,
  };
}

/**
 * Obtiene a partir de un DTO de un mensaje el Board asociado en el caso de existir.
 * @param dto
 */
export function transformBoard(dto: MessageDto): Board | undefined {
  if (typeof dto.content === 'undefined' || dto.content.components?.length === 0) return undefined;

  const { id, context, content, timestamp } = dto;

  return {
    id,
    timestamp,
    context,
    content,
  };
}

/**
 * Ordena los mensajes de menos reciente a mas reciente
 * @param a
 * @param b
 */
export function sortMessage(a: Message, b: Message) {
  return a.timestamp - b.timestamp;
}

/**
 * Determina si el message id iterado, es el mas reciente
 * @param messages
 * @param messageId
 */
export function isLatestMessage(messages: Message[], messageId: string) {
  return messages.length > 0 && messages[messages.length - 1].id === messageId;
}

/**
 * Calcula la fecha mas reciente de un arreglo de mensajes
 * @param messages
 */
export function calculateLatestDate(messages: Message[]) {
  return Math.max(...messages.map(({ timestamp }) => timestamp)) + 10;
}

/**
 * Transforma un mensaje o arreglo de mensajes en mensajes y boards
 * Handles intelligent filtering of PROGRESS messages based on incoming message types
 * @param state
 * @param newMessage
 * @param resetMessages
 */
export function transformMessages(
  state: InternalChatState,
  newMessage: MessageDto | MessageDto[] | Message | undefined,
  resetMessages: boolean = false,
): {
  messages: Message[];
} {
  const messagesDto = Array.isArray(newMessage) ? newMessage : typeof newMessage !== 'undefined' ? [newMessage] : [];
  const messagesTransformed = messagesDto.map(dto => transformMessage(dto)).filter(message => !(message.type === 'USER' && !message.text)); // Filter out USER messages without text

  if (resetMessages) {
    return {
      messages: messagesTransformed.sort(sortMessage),
    };
  }

  // Handle intelligent filtering for PROGRESS messages
  if (messagesTransformed.length > 0) {
    // Check message types in new messages
    const hasResponseMessage = messagesTransformed.some(m => !m.variant || m.variant === 'RESPONSE');
    const hasProgressMessage = messagesTransformed.some(m => m.variant === 'PROGRESS');

    // Create a temporary filtered state to avoid visual glitch
    let filteredMessages = state.messages;
    let messagesToAdd = messagesTransformed;

    // Filter old PROGRESS messages when we have a response OR new PROGRESS messages
    if (hasResponseMessage || hasProgressMessage) {
      // Filter out ALL old progress messages
      filteredMessages = state.messages.filter(msg => msg.variant !== 'PROGRESS');
    }

    // If we have multiple PROGRESS messages in new messages, only keep the latest one
    const progressMessages = messagesTransformed.filter(m => m.variant === 'PROGRESS');
    if (progressMessages.length > 1) {
      const latestProgress = progressMessages[progressMessages.length - 1];
      const nonProgressMessages = messagesTransformed.filter(m => m.variant !== 'PROGRESS');
      messagesToAdd = [...nonProgressMessages, latestProgress];
    }

    // Filter out duplicate messages by ID (for polling strategy)
    const existingIds = new Set(filteredMessages.map(m => m.id));
    const newUniqueMessages = messagesToAdd.filter(m => !existingIds.has(m.id));

    const messages = [...filteredMessages, ...newUniqueMessages];

    return {
      messages: messages.sort(sortMessage),
    };
  }

  // No new messages, return current state
  return {
    messages: state.messages.sort(sortMessage),
  };
}

/**
 * Verifica si en la lista de mensajes, hay un mensaje que fue enviado por el usuario
 * @param messages
 */
export function hasMessageSentFromUser(messages: Message[]): boolean {
  return messages.some(({ type, from }) => type === 'USER' && from !== 'BACKEND');
}

/**
 * Get a previous message by user with a specific ID
 * @param messages
 * @param messageId
 */
export function getPrevMessageByUserWithId(messages: Message[], messageId: string): Message | undefined {
  const indexMessage = messages.findIndex(({ id }) => id === messageId);
  return messages[indexMessage - 1];
}

/**
 * Save chatId and userId in session storage for auto-restore chat
 * @param chatId
 * @param userId
 */
export function saveChatAndUserId(chatId: string, userId: string): void {
  console.debug('Chat ID and User ID saved in sessionStorage:', chatId, userId);
  sessionStorage.setItem('chatId', chatId);
  sessionStorage.setItem('userId', userId);
}

/**
 * Save a message to restore after page reload
 * @param messageText
 */
export function saveMessageToRestore(messageText: string | undefined): void {
  if (typeof messageText === 'string') {
    console.debug('Save message to restore after page reload:', messageText);
    sessionStorage.setItem('messageToRestore', messageText);
  }
}

/**
 * Evaluate has a message to restore after page reload
 * @returns boolean
 */
export function messageToRestore(): { has: boolean; value: string | null } {
  const messageToRestore = sessionStorage.getItem('messageToRestore');

  return {
    has: messageToRestore !== null,
    value: messageToRestore,
  };
}

/**
 * Check if chatId and userId are stored in session storage
 * @returns boolean
 */
export function chatAndUserId(): { hasAll: boolean; hasChatId: boolean; hasUserId: boolean; chatId: string | null; userId: string | null } {
  const chatId = sessionStorage.getItem('chatId');
  const userId = sessionStorage.getItem('userId');

  return {
    hasAll: chatId !== null && userId !== null,
    hasChatId: chatId !== null,
    hasUserId: userId !== null,
    chatId: chatId,
    userId: userId,
  };
}

/**
 * Checks if the user is still waiting for a response from the assistant.
 * @param messages List of messages from the state.
 * @returns True if the last assistant message has 'PROGRESS' variant, otherwise false.
 */
export function isAssistantWaitingResponse(messages: Message[]): boolean {
  return messages
    .filter(m => m.isAssistant)
    .slice(-1) // Get the last assistant message
    .some(m => m.variant === 'PROGRESS');
}

/**
 * Decide whether the actions component for a message should be rendered
 * @param message the message to evaluate
 * @param messageIndex position of the message in the list
 */
export function showActionButtons(message: Message, messageIndex: number) {
  const showFeedbackButtons = messageIndex !== 0;
  const showDebugButton = !!message?.context?.debug;
  const showTSButton = initParams.sandbox;
  const showActions = message.isAssistant && message.variant !== 'PROGRESS' && !state.readonly && (showFeedbackButtons || showDebugButton || showTSButton);
  return {
    showActions,
    showFeedbackButtons,
    showDebugButton,
    showTSButton,
  };
}

/**
 * Type representing component data with a componentType field
 */
type ComponentWithType = {
  componentType?: string;
};

/**
 * Type guard to check if data has componentType property
 * @param data The data to check
 * @returns true if data is an object with componentType property
 */
export function hasComponentType(data: unknown): data is ComponentWithType {
  return typeof data === 'object' && data !== null && 'componentType' in data;
}

/**
 * Safely extracts componentType from message board components
 * @param message The message to extract componentType from
 * @returns The componentType string if found, undefined otherwise
 */
export function extractComponentType(message: Message | undefined): string | undefined {
  if (!message?.board?.content?.components) {
    return undefined;
  }

  const componentWithType = message.board.content.components.find(c => hasComponentType(c.data));

  return componentWithType && hasComponentType(componentWithType.data) ? componentWithType.data.componentType : undefined;
}
